<template>
  <div v-if="showOfflineMessage" class="error-message">
    <OfflineMessage message="No messages data" />
  </div>

  <div v-else-if="isLoading" class="loader-container">
    <div class="loader-title">
      <Loader2 />
      <span class="mt-2">Loading Messages...</span>
    </div>
  </div>

  <div v-else-if="transparentIfNoNotices && (!messages || messages.length === 0)"></div>

  <div v-else-if="errorLoading || !messages || messages.length === 0" class="error-message">
    <i class="material-icons-outlined align-middle mr-2">message</i>
    No messages available
  </div>

  <div v-else class="messages-wrapper" :style="wrapperStyle">
    <div class="messages-container">
      <div
        v-for="(message, index) in truncatedMessages"
        :key="message.externalId"
        class="messages-page"
        :class="{
          'initial-slide': currentPage === index + 1 && skipInitialAnimation,
          'hidden-slide': currentPage !== index + 1 && skipInitialAnimation,
          slideIn: currentPage === index + 1 && !skipInitialAnimation,
          slideOut: currentPage !== index + 1 && !skipInitialAnimation,
        }"
        v-show="currentPage === index + 1"
      >
        <div class="message">
          <div v-if="widgetData.messages.showTitle" class="message-title" :style="titleStyle">
            {{ message.title }}
          </div>
          <div
            :id="`message-body-${appId}-${index}`"
            class="message-body scrollbar"
            :style="messageBodyStyle"
            v-html="message.body"
          ></div>
        </div>
      </div>
    </div>

    <div class="slides" :style="paginationStyle">
      <template v-if="!widgetData.messages.paginationInText">
        <div
          v-for="index in messages.length"
          :key="index"
          class="page-dot"
          @click="setCurrentPage(index)"
        >
          <div
            :class="index === currentPage ? 'current-dot' : 'dot'"
            :style="{
              backgroundColor:
                index === currentPage
                  ? widgetData.messages.paginationFontColor &&
                    isHex8(widgetData.messages.paginationFontColor)
                    ? hex8ToRgba(widgetData.messages.paginationFontColor)
                    : widgetData.messages.paginationFontColor
                  : '#333',
            }"
          ></div>
        </div>
      </template>

      <div v-else class="pagination-in-text">
        {{ `Sida ${currentPage}/${messages.length}` }}
      </div>
    </div>

    <FloatingOfflineIndicator v-if="!isAppOnline" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue';
  import { CSSProperties } from 'vue/types/jsx';
  import localforage from 'localforage';

  import Loader2 from '@/components/common/Loader2.vue';
  import FloatingOfflineIndicator from '@/components/common/FloatingOfflineIndicator.vue';
  import OfflineMessage from '@/components/common/OfflineMessage.vue';

  import { apiGetMessages, apiGetSyncMessages } from '@/api/messagesBoard';
  import { getRBGAColor, hex8ToRgba, isHex8 } from '@/helpers/utils';
  import { cacheData } from '@/helpers/caching';
  import { createCyclicScroll } from '@/helpers/scrollHelper';

  import { TIME_UNITS } from '@/config/constants';
  import { ORGANISATION_WAIT_INTERVAL_MS, ORGANISATION_WAIT_MAX_ATTEMPTS } from '@/constants';
  import { messagesBoardDemoData } from '@/constants/messagesBoard';
  import {
    Message,
    MessagesConfig,
    WidgetData,
  } from '@/types/widgets/messagesWidget/messagesWidget';
  import { logger } from '@/utils/screenEventsLogger';
  import { truncateHtml } from '@/helpers/truncateHtml';

  export default defineComponent({
    name: 'MessagesBoardViewer',

    components: {
      Loader2,
      FloatingOfflineIndicator,
      OfflineMessage,
    },

    emits: ['saveData'],

    props: {
      widgetData: { type: Object as PropType<WidgetData>, default: () => ({}) },
      appId: { type: String, default: '' },
    },

    data() {
      return {
        timer: null as ReturnType<typeof setInterval> | null,
        refreshTimer: null as ReturnType<typeof setInterval> | null,
        offlineCheckTimer: null as ReturnType<typeof setInterval> | null,
        cyclicScroll: null as ReturnType<typeof createCyclicScroll> | null,
        currentPage: 1,
        isLoading: false,
        errorLoading: false,
        messages: [] as Message[],
        initialLoad: true,
        skipInitialAnimation: true,
        showOfflineMessage: false,
        lastFetchTimestamp: 0,
        isScrolling: false,
      };
    },

    mounted() {
      this.initMessagesView();

      if (!this.isAppOnline && !this.offlineCheckTimer) {
        this.startOfflineCheck();
      }

      // Start scrolling for the first slide after a short delay
      setTimeout(() => {
        if (this.messages && this.messages.length > 0) {
          this.startScrollTimer();
        }
      }, 500);
    },

    beforeUnmount() {
      this.destroyTimer();
      this.destroyRefreshTimer();
      this.clearOfflineCheckTimer();
      this.destroyScrollAnimation();
    },

    watch: {
      isAppOnline(newValue) {
        if (!newValue) {
          this.startOfflineCheck();
          return;
        }

        if (this.offlineCheckTimer) {
          this.initMessagesView();
          this.clearOfflineCheckTimer();
        }
      },
    },

    computed: {
      isAppOnline(): boolean {
        return this.$store.state.offline.isAppOnline;
      },

      storeMessages(): Message[] | null {
        return this.$store.state.player.appsData[this.appId]
          ? this.$store.state.player.appsData[this.appId].data.messages
          : null;
      },

      storeTimestamp(): number | null {
        return this.$store.state.player.appsData[this.appId]
          ? this.$store.state.player.appsData[this.appId].data.lastFetchTimestamp
          : null;
      },

      address(): { id: string; name: string } {
        return this.widgetData.messages.address;
      },

      notifyGlobalConfig(): any {
        const organisation = this.$store.getters.getOrganisation;
        if (!organisation || !organisation.sync || !organisation.sync.integrations) {
          return {};
        }

        const notifyIntegration = organisation.sync.integrations.find(
          (config: any) => config.type === 'notify',
        );
        if (!notifyIntegration) {
          return {};
        }

        return notifyIntegration.config || {};
      },

      messagesConfig(): MessagesConfig {
        return this.widgetData.messages;
      },

      messageBodyStyle(): CSSProperties {
        const fontColor = this.messagesConfig.bodyFontColor;

        return {
          fontFamily: this.messagesConfig.bodyFont,
          fontSize: `${this.messagesConfig.bodyFontSize}px`,
          lineHeight: `${this.messagesConfig.bodyFontSize * 1.5}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
          padding: `${this.messagesConfig.bodyVerticalPadding}px ${this.messagesConfig.bodyHorizontalPadding}px`,
        };
      },

      paginationStyle(): CSSProperties {
        const fontColor = this.messagesConfig.paginationFontColor;

        return {
          fontFamily: this.messagesConfig.paginationFont,
          fontSize: `${this.messagesConfig.paginationFontSize}px`,
          lineHeight: `${this.messagesConfig.paginationFontSize * 1.2}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
          justifyContent: this.messagesConfig.paginationAlignment,
          height: this.messagesConfig.paginationInText
            ? `${this.messagesConfig.paginationFontSize + 16}px`
            : 'auto',
          padding: `auto ${this.messagesConfig.bodyHorizontalPadding}px`,
        };
      },

      slideInterval(): number {
        return this.widgetData.messages ? this.widgetData.messages.interval : 15;
      },

      timeUnit(): string {
        return this.widgetData.messages ? this.widgetData.messages.timeUnit : 'days';
      },

      refreshTime(): number {
        return this.widgetData.messages ? this.widgetData.messages.refreshTime : 1;
      },

      titleStyle(): CSSProperties {
        const fontColor = this.messagesConfig.titleFontColor;
        const bgColor = this.messagesConfig.titleBackgroundColor;

        return {
          fontFamily: this.messagesConfig.titleFont,
          fontSize: `${this.messagesConfig.titleFontSize}px`,
          lineHeight: `${this.messagesConfig.titleFontSize * 1.3}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
          backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
          textAlign: this.messagesConfig.titleAlignment,
          padding: `${this.messagesConfig.titleVerticalPadding}px ${this.messagesConfig.titleHorizontalPadding}px`,
        };
      },

      wrapperStyle(): CSSProperties {
        return {
          backgroundColor: getRBGAColor(
            this.messagesConfig.backgroundColor,
            Number(this.messagesConfig.backgroundOpacity) / 100,
          ),
        };
      },

      maxWords(): number | null {
        return this.notifyGlobalConfig.maxWords || null;
      },

      transparentIfNoNotices(): boolean {
        return this.notifyGlobalConfig.transparentIfNoNotices || false;
      },

      truncatedMessages(): Message[] {
        if (!this.maxWords) return this.messages;
        return this.messages.map((message: Message) => ({
          ...message,
          body: truncateHtml(message.body, this.maxWords as number),
        }));
      },

      isSyncEnabled(): boolean {
        return !!(
          this.$store.getters.getOrganisation && this.$store.getters.getOrganisation.enable_sync
        );
      },
    },

    methods: {
      isHex8,
      hex8ToRgba,

      setCurrentPage(value: number) {
        this.currentPage = value;
      },

      nextMessagesSlide() {
        this.currentPage = this.currentPage < this.messages.length ? this.currentPage + 1 : 1;
        // Reset scroll position when changing slides
        this.resetScrollPosition();

        // Give the DOM time to update before starting the scroll
        setTimeout(() => {
          // Start scrolling for the new slide
          this.startScrollTimer();
        }, 100);
      },

      destroyTimer() {
        if (!this.timer) return;

        clearInterval(this.timer);
      },

      resetSlide() {
        this.destroyTimer();
        this.destroyScrollAnimation();
        this.setCurrentPage(1);
        this.resetScrollPosition();

        // Give the DOM time to update before starting the scroll
        setTimeout(() => {
          if (this.messages.length <= 1) {
            // If there's only one message, just start scrolling it
            this.startScrollTimer();
            return;
          }

          if (this.initialLoad) {
            this.initialLoad = false;

            // Start scrolling for the first slide
            this.startScrollTimer();

            setTimeout(() => {
              this.skipInitialAnimation = false;
              this.nextMessagesSlide();

              // Ensure at least 6 seconds for each slide to allow for scrolling
              const effectiveInterval = Math.max(this.slideInterval * 1000, 6000);
              this.timer = setInterval(this.nextMessagesSlide, effectiveInterval);
            }, this.slideInterval * 1000);
          } else {
            this.skipInitialAnimation = false;
            this.startScrollTimer();

            // Ensure at least 6 seconds for each slide to allow for scrolling
            const effectiveInterval = Math.max(this.slideInterval * 1000, 6000);
            this.timer = setInterval(this.nextMessagesSlide, effectiveInterval);
          }
        }, 100);
      },

      async initMessagesView() {
        this.errorLoading = false;
        try {
          if (this.isAppOnline) {
            if (!this.storeMessages || this.checkIfRefreshNeeded()) {
              await this.fetchMessages();
              return;
            }

            this.messages = this.filterMessagesByTags(this.storeMessages);
            this.resetSlide();
            this.setupRefreshTimer();
            return;
          }

          if (this.storeMessages && this.storeMessages.length > 0) {
            this.messages = this.filterMessagesByTags(this.storeMessages);
            this.resetSlide();
            return;
          }

          const data = await localforage.getItem(`messages-app-${this.appId}`);

          if (!data) {
            this.showOfflineMessage = true;
            return;
          }

          this.$emit('saveData', data);
          this.messages = this.filterMessagesByTags((data as any).messages || []);
          this.resetSlide();
          this.showOfflineMessage = false;
        } catch (error) {
          logger.addLog('initMessagesView - error: ' + error);
          this.errorLoading = true;
        }
      },

      checkIfRefreshNeeded() {
        const refreshIntervalMs = this.calculateRefreshIntervalMs();
        const refreshThreshold = Date.now() - refreshIntervalMs;

        if (!this.storeTimestamp) {
          return true;
        }

        return this.storeTimestamp <= refreshThreshold;
      },

      calculateRefreshIntervalMs() {
        if (!this.refreshTime || this.refreshTime <= 0) {
          return 60 * 60 * 1000; // Default to 1 hour if not specified
        }

        const timeUnit = this.timeUnit || TIME_UNITS.MINUTES.value;

        switch (timeUnit) {
          case TIME_UNITS.SECONDS.value:
            return this.refreshTime * 1000;
          case TIME_UNITS.MINUTES.value:
            return this.refreshTime * 60 * 1000;
          case TIME_UNITS.HOURS.value:
            return this.refreshTime * 60 * 60 * 1000;
          case TIME_UNITS.DAYS.value:
            return this.refreshTime * 24 * 60 * 60 * 1000;
          default:
            return this.refreshTime * 60 * 1000;
        }
      },

      startOfflineCheck() {
        if (this.offlineCheckTimer) {
          clearInterval(this.offlineCheckTimer);
        }

        this.offlineCheckTimer = setInterval(this.initMessagesView, 2 * 60 * 1000);
      },

      clearOfflineCheckTimer() {
        if (!this.offlineCheckTimer) return;

        clearInterval(this.offlineCheckTimer);
        this.offlineCheckTimer = null;
      },

      async fetchMessages(attempt = 0) {
        if (this.messagesConfig.address.id === 'demo') {
          this.saveMessagesData(messagesBoardDemoData);
          this.messages = this.filterMessagesByTags(messagesBoardDemoData);
          this.resetSlide();
          this.setupRefreshTimer();
          return;
        }

        this.errorLoading = false;
        this.isLoading = true;

        const organisationLoaded = this.$store.getters.isOrganisationLoaded;

        if (!organisationLoaded && attempt < ORGANISATION_WAIT_MAX_ATTEMPTS) {
          setTimeout(() => this.fetchMessages(attempt + 1), ORGANISATION_WAIT_INTERVAL_MS);
          return;
        }

        try {
          let response;
          if (this.isSyncEnabled) {
            response = await apiGetSyncMessages(this.messagesConfig.address.id);
          } else {
            response = await apiGetMessages(this.messagesConfig.address.id);
          }

          this.saveMessagesData(response);
          this.messages = this.filterMessagesByTags(response);
          this.resetSlide();
          this.showOfflineMessage = false;
        } catch (error) {
          logger.addLog('FetchMessages - error: ' + error);
          this.errorLoading = true;

          const cachedData = await localforage.getItem(`messages-app-${this.appId}`);

          if (cachedData) {
            this.messages = this.filterMessagesByTags((cachedData as any).messages || []);
            this.resetSlide();
          }
        }

        this.isLoading = false;
        this.setupRefreshTimer();
      },

      filterMessagesByTags(messages: Message[]): Message[] {
        var configTags = this.messagesConfig.tags;
        if (!configTags || !Array.isArray(configTags) || configTags.length === 0) {
          return messages;
        }
        var tags = configTags as string[];
        return messages.filter(function (message: Message) {
          if (!message.tags || !Array.isArray(message.tags)) return false;
          for (var i = 0; i < message.tags.length; i++) {
            if (tags.indexOf(message.tags[i]) !== -1) return true;
          }
          return false;
        });
      },

      saveMessagesData(messages: Message[]): void {
        const dataWithTimestamp = { messages, lastFetchTimestamp: Date.now() };
        cacheData(`messages-app-${this.appId}`, dataWithTimestamp);
        this.$emit('saveData', dataWithTimestamp);
      },

      resetScrollPosition() {
        // Reset all message body elements to top
        const messageBodyElements = document.querySelectorAll('.message-body');
        messageBodyElements.forEach((element) => {
          const el = element as HTMLElement;
          el.scrollTop = 0;
        });
        this.isScrolling = false;
      },

      getCurrentMessageBody() {
        // First try to get by ID which is most reliable
        const currentIndex = this.currentPage - 1;
        const messageBodyId = `message-body-${this.appId}-${currentIndex}`;
        const messageBody = document.getElementById(messageBodyId);

        if (messageBody) {
          return messageBody as HTMLElement;
        }

        const visibleMessagePage = document.querySelector(
          '.messages-page[style*="display: block"], .messages-page[style*="display:block"]',
        );

        if (!visibleMessagePage) {
          const messagePages = document.querySelectorAll('.messages-page');

          if (messagePages[currentIndex]) {
            return messagePages[currentIndex].querySelector('.message-body') as HTMLElement;
          }
        } else {
          return visibleMessagePage.querySelector('.message-body') as HTMLElement;
        }
      },

      startScrollTimer() {
        this.destroyScrollAnimation();

        const messageBody = this.getCurrentMessageBody();
        if (!messageBody) {
          return;
        }

        if (messageBody.scrollHeight <= messageBody.clientHeight) {
          return;
        }

        this.cyclicScroll = createCyclicScroll({
          element: messageBody,
          initialDelay: 3000,
          bottomPause: 3000,
          topPause: 1000,
          durationMultiplier: 20,
          minDuration: 3000,
          maxDuration: 15000,
          onStop: () => {
            this.isScrolling = false;
          },
        });

        this.isScrolling = true;
        this.cyclicScroll.startScrollAnimation();
      },

      destroyScrollAnimation() {
        if (this.cyclicScroll) {
          this.cyclicScroll.stopScrollAnimation();
          this.cyclicScroll = null;
        }
        this.isScrolling = false;
      },

      destroyRefreshTimer() {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer);
          this.refreshTimer = null;
        }
      },

      setupRefreshTimer() {
        this.destroyRefreshTimer();

        if (
          !this.refreshTime ||
          this.refreshTime <= 0 ||
          this.widgetData.messages.address.id === 'demo'
        ) {
          return;
        }

        let refreshIntervalMs = 0;
        const timeUnit = this.timeUnit || TIME_UNITS.MINUTES.value;

        switch (timeUnit) {
          case TIME_UNITS.SECONDS.value:
            refreshIntervalMs = this.refreshTime * 1000;
            break;
          case TIME_UNITS.MINUTES.value:
            refreshIntervalMs = this.refreshTime * 60 * 1000;
            break;
          case TIME_UNITS.HOURS.value:
            refreshIntervalMs = this.refreshTime * 60 * 60 * 1000;
            break;
          case TIME_UNITS.DAYS.value:
            refreshIntervalMs = this.refreshTime * 24 * 60 * 60 * 1000;
            break;
          default:
            refreshIntervalMs = this.refreshTime * 60 * 1000;
        }

        this.refreshTimer = setInterval(() => {
          this.fetchMessages();
        }, refreshIntervalMs);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    padding: 16px;
    background-color: $lightgrey;

    .loader-title {
      display: flex;
      flex-direction: column;
      font-size: 16px;
      text-align: center;
      color: #151515;
    }
  }

  .error-message {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    text-align: center;
    height: 100%;
    width: 100%;
    color: #151515;
    background-color: $lightgrey;
  }

  .messages-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .messages-container {
    position: relative;
    flex: 1;
    overflow: hidden;
  }

  .messages-page {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &.slideIn {
      animation: slideIn 0.5s forwards;
    }

    &.slideOut {
      animation: slideOut 0.5s forwards;
    }

    &.initial-slide {
      z-index: 2;
    }

    &.hidden-slide {
      z-index: 1;
      transform: translateX(-100%);
    }
  }

  .message {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    .message-title {
      font-weight: bold;
    }

    .message-body {
      overflow-y: auto;
      flex-grow: 1;

      ::v-deep(img) {
        max-width: 100%;
        height: auto;
      }

      ::v-deep(a) {
        color: #3498db;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      ::v-deep(h1),
      ::v-deep(h2),
      ::v-deep(h3),
      ::v-deep(h4),
      ::v-deep(h5),
      ::v-deep(h6) {
        font-family: inherit;
        font-size: inherit;
      }
    }
  }

  .slides {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 20px;
    width: 100%;
    flex-wrap: wrap;

    .page-dot {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 34px;
      height: 20px;
      border-radius: 100%;
      padding: 3px 10px;

      &:hover {
        cursor: pointer;

        .dot {
          background-color: '#151515';
          opacity: 40%;
        }
      }
    }

    .dot,
    .current-dot {
      background-color: '#151515';
      border-radius: 100%;
    }

    .dot {
      opacity: 20%;
      width: 8px;
      height: 8px;
    }

    .current-dot {
      background-color: '#151515';
      width: 14px;
      height: 14px;
    }
  }

  .scrollbar {
    /* For Firefox Browser */
    scrollbar-width: thin;
    scrollbar-color: #b7b8b7;

    /* For Chrome, EDGE, Opera, Others */
    &::-webkit-scrollbar {
      scrollbar-width: thin;
      scrollbar-color: #b7b8b7;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #b7b8b7;
      border-radius: 4px;

      &:hover {
        cursor: pointer;
        background-color: #d9d9d9;
      }
    }

    &::-webkit-scrollbar-track {
      border-radius: 10px;
    }
  }

  @keyframes slideIn {
    0% {
      transform: translateX(100%);
      z-index: 2;
    }
    100% {
      transform: translateX(0);
      z-index: 2;
    }
  }

  @keyframes slideOut {
    0% {
      transform: translateX(0);
      z-index: 1;
    }
    100% {
      transform: translateX(-100%);
      z-index: 1;
    }
  }
</style>
